/** Renders a batch-edit task result: outcome counts + a bounded per-row table
 *  with each row's `from → to` changes, conditional notes, or error. The full
 *  report (including per-condition debug traces) lives on the task — the
 *  header links there.
 */
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Box,
  Chip,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { BatchEditResultEntry, BatchEditTaskResult } from '@/api/batchEdit';
import { OUTCOME_LABELS, OUTCOME_SEVERITY } from '@/api/batchEdit';

const MAX_ROWS_SHOWN = 50;

function fmt(value: unknown): string {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'string') return value.length > 40 ? `${value.slice(0, 40)}…` : value;
  return JSON.stringify(value);
}

function rowDetail(entry: BatchEditResultEntry): string {
  if (entry.error) return entry.error;
  const changes = (entry.blocks ?? []).flatMap((b) => b.changes);
  if (changes.length === 0) return '';
  return changes.map((c) => `${c.path}: ${fmt(c.from)} → ${fmt(c.to)}`).join(' · ');
}

export function DryRunReport({ result, taskId }: { result: BatchEditTaskResult; taskId: string }) {
  const rows = result.results.slice(0, MAX_ROWS_SHOWN);
  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
        {Object.entries(result.counts).map(([code, n]) => (
          <Chip
            key={code}
            size="small"
            color={OUTCOME_SEVERITY[code as keyof typeof OUTCOME_SEVERITY] ?? 'default'}
            variant="outlined"
            label={`${OUTCOME_LABELS[code as keyof typeof OUTCOME_LABELS] ?? code}: ${n.toLocaleString()}`}
          />
        ))}
        <Box sx={{ flexGrow: 1 }} />
        <Link component={RouterLink} to={`/bgtasks/${taskId}`} variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          Full report <LaunchIcon sx={{ fontSize: 14 }} />
        </Link>
      </Stack>
      <Box sx={{ maxHeight: 280, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 90 }}>ID</TableCell>
              <TableCell sx={{ width: 200 }}>Record</TableCell>
              <TableCell sx={{ width: 150 }}>Outcome</TableCell>
              <TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((entry) => (
              <TableRow key={String(entry.id)} hover>
                <TableCell>{entry.id}</TableCell>
                <TableCell>
                  <Typography variant="body2" noWrap title={entry.repr ?? undefined}>
                    {entry.repr ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={OUTCOME_SEVERITY[entry.outcome] ?? 'default'}
                    label={OUTCOME_LABELS[entry.outcome] ?? entry.outcome}
                  />
                </TableCell>
                <TableCell>
                  <Tooltip title={rowDetail(entry)}>
                    <Typography variant="body2" noWrap sx={{ maxWidth: 380 }}>
                      {rowDetail(entry) || '—'}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      {(result.results.length > MAX_ROWS_SHOWN || result.results_truncated) && (
        <Typography variant="caption" color="text.secondary">
          Showing the first {rows.length.toLocaleString()} of {result.total.toLocaleString()} row(s) —
          the task holds the full report{result.results_truncated ? ' (first 1,000 rows)' : ''}.
        </Typography>
      )}
    </Box>
  );
}
